import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { useAuth } from '../lib/auth'
import { ChangeList, fmtWhen } from '../components/RecordHistory'

// Activity log - complete accountability (Sahin 2026-10-25). Owner + admin only.
//   Changes      every change request from a screen and every row it changed
//                (audit_log, migration 076)
//   Script runs  every run of a script: cron, the 06:00 pipeline, a screen
//                button or by hand (script_runs, migration 077)
// Nothing was recorded before 2026-10-25.

interface LogRow {
  id: number
  at: string
  source: 'api' | 'db' | 'script'
  actor: string
  viewed_as_by: string | null
  request_id: string | null
  action: string
  entity: string | null
  entity_id: string | null
  status: number | null
  ip: string | null
  details: Record<string, unknown> | null
}
interface LogResp { rows: LogRow[]; has_more: boolean; facets: { actors: string[]; entities: string[] } }

interface RunRow {
  run_id: number
  script: string
  args: string | null
  started_by: string
  parent_run_id: number | null
  host: string | null
  started_at: string
  ended_at: string | null
  status: 'running' | 'ok' | 'failed'
  exit_code: number | null
  error: string | null
  rows_changed: Record<string, Record<string, number>> | null
  children: number
}
interface RunsResp { rows: RunRow[]; has_more: boolean; scripts: string[] }

// plain names for the audited tables
const TABLE_LABEL: Record<string, string> = {
  customer_rebills: 'Rebill', carrier_disputes: 'Dispute', carrier_claims: 'Claim',
  carrier_claim_credit_lines: 'Claim credit line', customer_invoices: 'Customer invoice',
  customer_invoice_items: 'Invoice line', customers: 'Customer', app_settings: 'Setting',
  rebill_generator_settings: 'Rebill generator switch', carrier_dispute_rules: 'Dispute rules',
  carrier_dispute_contacts: 'Dispute contact', byelabel_push_exclusions: 'Push exclusion',
  users: 'User', user_permissions: 'User permissions', operating_costs: 'Operating cost',
  personnel: 'Person', personnel_salary: 'Salary', affiliate_partners: 'Affiliate',
  affiliate_agreements: 'Affiliate agreement', affiliate_payments: 'Affiliate payment',
  affiliate_payment_lines: 'Affiliate payment line', affiliate_payment_installments: 'Affiliate installment',
  customer_affiliate_attribution: 'Affiliate attribution', invoice_downloads: 'Invoice download',
  chase_transactions: 'Bank transaction', carrier_charge_rules: 'Charge rule',
  carrier_detection_settings: 'Detection setting', suspicious_charge_dismissals: 'Ignored charge',
  dispute_email_log: 'Dispute e-mail',
}
const VERB: Record<string, string> = { INSERT: 'created', UPDATE: 'changed', DELETE: 'deleted', TRUNCATE: 'emptied (whole table)' }

function describe(r: LogRow): string {
  if (r.source === 'db') {
    const what = TABLE_LABEL[r.entity ?? ''] ?? r.entity ?? '?'
    return `${what}${r.entity_id ? ` ${r.entity_id}` : ''} ${VERB[r.action] ?? r.action.toLowerCase()}`
  }
  return r.action
}

function whoKind(actor: string): string {
  if (actor.startsWith('db:script:')) return 'script'
  if (actor.startsWith('db:')) return 'database'
  return ''
}

function fmtDuration(a: string, b: string | null): string {
  if (!b) return ''
  const s = Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000)
  return s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

const inputCls = 'h-8 px-2 text-[12.5px] border border-ink-200 rounded-md bg-white'

export function AuditLogView() {
  const { isAdmin } = useAuth()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'runs' ? 'runs' : 'changes'

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <Icon name="eye-off" className="w-7 h-7 text-ink-400 mb-4" />
        <h2 className="text-[18px] font-bold text-ink-900 mb-2">Owner and admin only</h2>
        <p className="text-sm text-ink-500 max-w-sm">The activity log shows every user's actions.</p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-5">
        <div className="text-[12px] font-semibold text-flame-600 uppercase tracking-wider">System</div>
        <h1 className="text-[28px] font-bold tracking-tight mt-1">Activity log</h1>
        <p className="text-ink-500 text-[13px] mt-1">
          Who changed what, when and from where - every screen action, every record it changed, and every script run.
          Kept forever; nobody can edit or delete it. Recording started on 25 Oct 2026.
        </p>
      </div>
      <div className="flex gap-1 border-b border-ink-100 mb-4">
        {([['changes', 'Changes'], ['runs', 'Script runs']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setParams(k === 'runs' ? { tab: 'runs' } : {})}
                  className={`px-4 py-2 text-[13px] -mb-px border-b-2 ${tab === k ? 'border-royal-500 text-ink-900 font-semibold' : 'border-transparent text-ink-500 hover:text-ink-800'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'changes' ? <ChangesTab /> : <RunsTab />}
    </div>
  )
}

function ChangesTab() {
  const [f, setF] = useState({ actor: '', entity: '', entity_id: '', source: '', from: '', to: '', q: '', request_id: '', show_refused: false })
  const [applied, setApplied] = useState(f)
  const [limit, setLimit] = useState(200)
  const [open, setOpen] = useState<number | null>(null)

  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(applied)) {
    if (k === 'show_refused') { if (v) qs.set(k, '1') } else if (v) qs.set(k, String(v))
  }
  qs.set('limit', String(limit))
  const { data, isLoading, error } = useQuery<LogResp>({
    queryKey: ['activity', qs.toString()],
    queryFn: async () => {
      const r = await fetch(`/api/activity?${qs}`)
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
      return r.json()
    },
  })
  const apply = (next = f) => { setApplied(next); setLimit(200) }
  const set = (k: keyof typeof f, v: string | boolean) => setF(p => ({ ...p, [k]: v }))
  const showRequest = (rid: string) => { const next = { ...f, request_id: rid }; setF(next); apply(next) }

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">Who<br />
          <select value={f.actor} onChange={e => set('actor', e.target.value)} className={`${inputCls} w-64`}>
            <option value="">Everyone</option>
            {data?.facets.actors.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">What<br />
          <select value={f.entity} onChange={e => set('entity', e.target.value)} className={`${inputCls} w-48`}>
            <option value="">All records</option>
            {data?.facets.entities.map(e => <option key={e} value={e}>{TABLE_LABEL[e] ?? e}</option>)}
          </select>
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">Record id<br />
          <input value={f.entity_id} onChange={e => set('entity_id', e.target.value)} className={`${inputCls} w-28`} placeholder="e.g. 132" />
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">Kind<br />
          <select value={f.source} onChange={e => set('source', e.target.value)} className={`${inputCls} w-40`}>
            <option value="">Actions + changes</option>
            <option value="api">Screen actions</option>
            <option value="db">Record changes</option>
          </select>
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">From<br />
          <input type="date" value={f.from} onChange={e => set('from', e.target.value)} className={inputCls} />
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">To<br />
          <input type="date" value={f.to} onChange={e => set('to', e.target.value)} className={inputCls} />
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">Text<br />
          <input value={f.q} onChange={e => set('q', e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()}
                 className={`${inputCls} w-44`} placeholder="tracking, note, route…" />
        </label>
        <label className="flex items-center gap-1.5 text-[12px] text-ink-600 h-8">
          <input type="checkbox" checked={f.show_refused} onChange={e => set('show_refused', e.target.checked)} />
          refused, not signed in
        </label>
        <button onClick={() => apply()} className="h-8 px-4 rounded-md bg-ink-900 text-white text-[12.5px] font-medium">Apply</button>
        <button onClick={() => { const e = { actor: '', entity: '', entity_id: '', source: '', from: '', to: '', q: '', request_id: '', show_refused: false }; setF(e); apply(e) }}
                className="h-8 px-3 rounded-md border border-ink-200 text-[12.5px] text-ink-600">Clear</button>
      </div>
      {applied.request_id && (
        <div className="mb-3 text-[12px] text-ink-600 bg-royal-50 rounded-md px-3 py-2">
          Showing one action and everything it changed.{' '}
          <button className="underline" onClick={() => { const n = { ...f, request_id: '' }; setF(n); apply(n) }}>Show all</button>
        </div>
      )}

      {isLoading ? <p className="text-[13px] text-ink-400">Loading…</p>
       : error ? <p className="text-[13px] text-flame-600">{(error as Error).message}</p>
       : !data?.rows.length ? <p className="text-[13px] text-ink-500">Nothing recorded for this filter.</p> : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] font-semibold text-ink-400 uppercase border-b border-ink-100">
              <th className="py-2 pr-3 w-40">When</th><th className="py-2 pr-3">Who</th><th className="py-2 pr-3">What</th>
              <th className="py-2 pr-3 w-20">Result</th><th className="py-2 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <Fragment key={r.id}>
                <tr className={`border-b border-ink-50 align-top cursor-pointer hover:bg-ink-50 ${r.source === 'db' ? 'text-ink-600' : ''}`}
                    onClick={() => setOpen(open === r.id ? null : r.id)}>
                  <td className="py-2 pr-3 whitespace-nowrap text-ink-500">{fmtWhen(r.at)}</td>
                  <td className="py-2 pr-3 break-all">
                    {r.actor}
                    {whoKind(r.actor) && <span className="ml-1 text-[10.5px] text-ink-400">({whoKind(r.actor)})</span>}
                    {r.viewed_as_by && <div className="text-[10.5px] text-honey-600">viewed as, by owner {r.viewed_as_by}</div>}
                  </td>
                  <td className="py-2 pr-3">
                    {r.source === 'db' && <span className="text-ink-300 mr-1">↳</span>}
                    {describe(r)}
                    {r.source === 'api' && r.entity_id && <span className="text-ink-400"> · {r.entity_id}</span>}
                  </td>
                  <td className="py-2 pr-3">
                    {r.status != null && (
                      <span className={`text-[11px] font-medium ${r.status < 400 ? 'text-mint-600' : 'text-flame-600'}`}>
                        {r.status < 400 ? 'OK' : 'refused'} {r.status}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {r.request_id && !applied.request_id && (
                      <button onClick={e => { e.stopPropagation(); showRequest(r.request_id!) }}
                              className="text-[11px] text-royal-600 hover:underline">linked</button>
                    )}
                  </td>
                </tr>
                {open === r.id && (
                  <tr className="border-b border-ink-100 bg-ink-50">
                    <td colSpan={5} className="px-3 py-2">
                      {r.source === 'db'
                        ? <ChangeList action={r.action} details={r.details} />
                        : <pre className="text-[11px] text-ink-700 whitespace-pre-wrap break-all">{JSON.stringify(r.details, null, 2)}</pre>}
                      <div className="text-[10.5px] text-ink-400 mt-1">
                        {r.ip && <>from {r.ip} · </>}
                        {r.request_id?.startsWith('run:') ? <>script run #{r.request_id.slice(4)} · </> : null}
                        entry #{r.id}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
      {data?.has_more && limit < 1000 && (
        <button onClick={() => setLimit(l => Math.min(l + 200, 1000))} className="mt-3 text-[12.5px] text-royal-600 hover:underline">Show more</button>
      )}
    </Card>
  )
}

function RunsTab() {
  const [f, setF] = useState({ script: '', status: '', started_by: '', from: '', to: '' })
  const [applied, setApplied] = useState(f)
  const [parent, setParent] = useState<number | null>(null)
  const [limit, setLimit] = useState(200)

  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(applied)) if (v) qs.set(k, v)
  if (parent) qs.set('parent', String(parent))
  qs.set('limit', String(limit))
  const { data, isLoading, error } = useQuery<RunsResp>({
    queryKey: ['activity-runs', qs.toString()],
    queryFn: async () => {
      const r = await fetch(`/api/activity/runs?${qs}`)
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
      return r.json()
    },
  })
  const set = (k: keyof typeof f, v: string) => setF(p => ({ ...p, [k]: v }))

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">Script<br />
          <select value={f.script} onChange={e => set('script', e.target.value)} className={`${inputCls} w-64`}>
            <option value="">All scripts</option>
            {data?.scripts.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">Result<br />
          <select value={f.status} onChange={e => set('status', e.target.value)} className={`${inputCls} w-32`}>
            <option value="">All</option><option value="ok">OK</option><option value="failed">Failed</option>
            <option value="running">Running / no end</option>
          </select>
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">Started by<br />
          <input value={f.started_by} onChange={e => set('started_by', e.target.value)} className={`${inputCls} w-44`} placeholder="cron, e-mail…" />
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">From<br />
          <input type="date" value={f.from} onChange={e => set('from', e.target.value)} className={inputCls} />
        </label>
        <label className="text-[10.5px] font-semibold text-ink-400 uppercase">To<br />
          <input type="date" value={f.to} onChange={e => set('to', e.target.value)} className={inputCls} />
        </label>
        <button onClick={() => { setApplied(f); setLimit(200) }} className="h-8 px-4 rounded-md bg-ink-900 text-white text-[12.5px] font-medium">Apply</button>
      </div>
      {parent && (
        <div className="mb-3 text-[12px] text-ink-600 bg-royal-50 rounded-md px-3 py-2">
          Steps of run #{parent}. <button className="underline" onClick={() => setParent(null)}>Back to all runs</button>
        </div>
      )}
      {isLoading ? <p className="text-[13px] text-ink-400">Loading…</p>
       : error ? <p className="text-[13px] text-flame-600">{(error as Error).message}</p>
       : !data?.rows.length ? <p className="text-[13px] text-ink-500">No script runs recorded for this filter.</p> : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] font-semibold text-ink-400 uppercase border-b border-ink-100">
              <th className="py-2 pr-3 w-40">Started</th><th className="py-2 pr-3">Script</th><th className="py-2 pr-3">Started by</th>
              <th className="py-2 pr-3 w-24">Result</th><th className="py-2 pr-3">Rows changed</th><th className="py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => {
              const stale = r.status === 'running' && Date.now() - new Date(r.started_at).getTime() > 6 * 3600_000
              return (
                <tr key={r.run_id} className="border-b border-ink-50 align-top">
                  <td className="py-2 pr-3 whitespace-nowrap text-ink-500">
                    {fmtWhen(r.started_at)}
                    <div className="text-[10.5px] text-ink-400">#{r.run_id}{r.ended_at && ` · ${fmtDuration(r.started_at, r.ended_at)}`}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <span className="font-medium">{r.script}</span>
                    {r.args && <div className="text-[10.5px] text-ink-400 break-all mono">{r.args}</div>}
                  </td>
                  <td className="py-2 pr-3 break-all">{r.started_by}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-[11px] font-medium ${r.status === 'ok' ? 'text-mint-600' : r.status === 'failed' ? 'text-flame-600' : 'text-honey-600'}`}>
                      {r.status === 'ok' ? 'OK' : r.status === 'failed' ? `Failed (${r.exit_code})` : stale ? 'No end recorded (stopped or killed)' : 'Running'}
                    </span>
                    {r.error && <div className="text-[10.5px] text-flame-600 break-words">{r.error}</div>}
                  </td>
                  <td className="py-2 pr-3 text-[11.5px] text-ink-600">
                    {r.rows_changed
                      ? Object.entries(r.rows_changed).map(([t, c]) => (
                          <div key={t}>{TABLE_LABEL[t] ?? t}: {Object.entries(c).map(([k, n]) => `${n.toLocaleString()} ${k}`).join(', ')}</div>
                        ))
                      : <span className="text-ink-300">—</span>}
                  </td>
                  <td className="py-2 text-right">
                    {r.children > 0 && (
                      <button onClick={() => setParent(r.run_id)} className="text-[11px] text-royal-600 hover:underline">{r.children} steps</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
      {data?.has_more && limit < 1000 && (
        <button onClick={() => setLimit(l => Math.min(l + 200, 1000))} className="mt-3 text-[12.5px] text-royal-600 hover:underline">Show more</button>
      )}
    </Card>
  )
}
